import React, { useState } from 'react';
import { Card, Button, Tag, Space, Typography, Image } from 'antd';
import { HeartOutlined, HeartFilled, ShoppingCartOutlined, EyeOutlined, StarOutlined, ShareAltOutlined, RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { getImageWithFallback, handleImageError, getAllImages } from '../../utils/images';
import AIInfoButton from './AIInfoButton';
import ImageCarousel from './ImageCarousel';

const { Text } = Typography;

function FoodCard({
    variant = 'menu', // menu | vendor | category | product
    item = {},
    imageHeight = 200,
    layout = 'standard', // compact | standard | elevated
    actionsPlacement = 'footer', // inline | footer
    showFavorite = true,
    showShare = false,
    onView,
    onAddToCart,
    onFavorite,
    onShare,
    isFavorite = false,
    badges = [], // e.g., [{ text: 'Veg', color: 'green' }]
}) {
    const [isHovered, setIsHovered] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    
    const title = item.name || item.title || item.business_name || '—';
    const subtitle = item.vendor?.business_name || item.category?.name || item.subtitle || '';
    const price = typeof item.price !== 'undefined' && item.price !== null ? item.price : undefined;
    const minPrice = typeof item.min_price !== 'undefined' && item.min_price !== null ? item.min_price : undefined;
    const maxPrice = typeof item.max_price !== 'undefined' && item.max_price !== null ? item.max_price : undefined;
    const rating = typeof item.rating !== 'undefined' ? item.rating : undefined;
    const imageUrl = getImageWithFallback(item, 'medium');
    const allImages = getAllImages(item, 'medium');
    const hasMultipleImages = allImages.length > 1;
    
    // Debug logging
    console.log('FoodCard Debug:', {
        itemName: item.name,
        allImages: allImages,
        hasMultipleImages: hasMultipleImages,
        imagesArray: item.images
    });


    return (
        <Card
            hoverable
            className={`modern-food-card ${layout === 'elevated' ? 'shadow-lg' : ''} w-full`}
            style={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: isHovered 
                    ? '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0,0,0,0.08)' 
                    : '0 2px 8px rgba(0,0,0,0.06)',
                background: '#ffffff',
                minHeight: '300px',
                maxWidth: '100%'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            bodyStyle={{ padding: 0 }}
        >
            {/* Image Section with Modern Styling */}
            <div 
                className="relative cursor-pointer group flex items-center justify-center"
                style={{ 
                    height: `${imageHeight}px`, 
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: '#f8f9fa'
                }}
                onClick={() => onView?.(item)}
            >
                {hasMultipleImages ? (
                    <div className="relative w-full h-full">
                        <ImageCarousel
                            item={item}
                            size="medium"
                            height={imageHeight}
                            showIndicators={true}
                            showArrows={false}
                            showPreview={true}
                            autoplay={false}
                            maxImages={3}
                            onImageClick={(image, index) => {
                                console.log('Image clicked:', image, index);
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {/* Image with loading state */}
                        <div 
                            className={`absolute inset-0 transition-opacity duration-300 ${
                                imageLoaded ? 'opacity-0' : 'opacity-100'
                            }`}
                            style={{
                                backgroundColor: '#000000',
                                background: 'linear-gradient(90deg, #333333 25%, #000000 50%, #333333 75%)',
                                backgroundSize: '200% 100%',
                                animation: imageLoaded ? 'none' : 'loading 1.5s infinite'
                            }}
                        />
                        <Image
                            src={imageUrl}
                            alt={title}
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                objectPosition: 'center',
                                transition: 'transform 0.3s ease',
                                transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                                opacity: imageLoaded ? 1 : 0,
                                backgroundColor: 'transparent'
                            }}
                            preview={false}
                            fallback="/images/placeholder.svg"
                            onError={handleImageError}
                            onLoad={() => setImageLoaded(true)}
                        />
                    </>
                )}
                
                {/* Gradient Overlay */}
                <div 
                    className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"
                    style={{ opacity: isHovered ? 1 : 0.7 }}
                />
                
                {/* Top Right Actions */}
                <div className="absolute top-3 right-3 flex flex-col gap-2">
                    {showFavorite && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onFavorite?.(item);
                            }}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all backdrop-blur-sm ${
                                isFavorite 
                                    ? 'bg-red-500 text-white shadow-lg' 
                                    : 'bg-white/90 text-gray-600 hover:bg-white'
                            }`}
                        >
                            {isFavorite ? <HeartFilled className="text-sm" /> : <HeartOutlined className="text-sm" />}
                        </button>
                    )}
                </div>

                {/* Badges - Top Left */}
                {badges?.length > 0 && (
                    <div className="absolute top-3 left-3 flex gap-1.5">
                        {badges.slice(0, 2).map((b, i) => (
                            <span 
                                key={i}
                                className="px-2.5 py-1 text-xs font-semibold rounded-md"
                                style={{ 
                                    backgroundColor: b.color === 'green' ? '#16a34a' : 
                                                   b.color === 'red' ? '#C97D60' : 
                                                   b.color === 'blue' ? '#2563eb' : '#6b7280',
                                    color: '#ffffff'
                                }}
                            >
                                {b.text}
                            </span>
                        ))}
                    </div>
                )}
                

                {/* Hover Actions Overlay */}
                <div 
                    className="absolute inset-0 bg-black/20 transition-all duration-300 flex items-center justify-center"
                    style={{ opacity: isHovered ? 1 : 0 }}
                >
                    <div className="flex gap-3">
                        {onView && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onView(item);
                                }}
                                className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                            >
                                <EyeOutlined className="text-blue-500" />
                            </button>
                        )}
                        {onAddToCart && variant === 'menu' && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddToCart(item);
                                }}
                                className="w-12 h-12 bg-[#C97D60] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-300"
                                style={{ background: '#C97D60' }}
                            >
                                <ShoppingCartOutlined className="text-white text-lg" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Content Section - Matching Reference Design */}
            <div className="p-4">
                {/* Dish Name with Icon */}
                <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-[#4A4A4A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <h3 className="text-base font-semibold text-[#2C2C2C] truncate">
                        {title}
                    </h3>
                </div>

                {/* Catering Partner */}
                {subtitle && (
                    <p className="text-sm text-[#4A4A4A] mb-2">
                        {subtitle}
                    </p>
                )}

                {/* Description */}
                {item.description && (
                    <p className="text-sm text-[#4A4A4A] line-clamp-2 mb-4 leading-relaxed">
                        {item.description}
                    </p>
                )}

                {/* Bottom Row - Actions */}
                <div className="action-buttons-container" style={{
                    display: 'grid',
                    justifyItems: 'center',
                    alignItems: 'end',
                    justifyContent: 'center',
                    alignContent: 'space-evenly',
                    gap: '8px'
                }}>
                    {actionsPlacement === 'footer' && (
                        <div className="action-buttons-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto auto auto',
                            gap: '8px',
                            justifyItems: 'center',
                            alignItems: 'end',
                            justifyContent: 'center',
                            alignContent: 'space-evenly',
                            width: '100%'
                        }}>
                            {variant === 'menu' && (
                                <AIInfoButton
                                    menuItem={item}
                                    type="default"
                                    size="small"
                                    variant="outlined"
                                    className="px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5"
                                    showCustomChat={false}
                                >
                                    Ask AI
                                </AIInfoButton>
                            )}
                            {onView && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onView(item);
                                    }}
                                    className="px-3 py-1.5 text-[#2C2C2C] text-sm font-medium rounded-lg border border-gray-300 hover:border-[#C97D60] hover:text-[#C97D60] transition-colors flex items-center gap-1"
                                >
                                    <EyeOutlined className="text-xs" />
                                    View
                                </button>
                            )}
                            {onAddToCart && variant === 'menu' && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddToCart(item);
                                    }}
                                    className="px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all duration-300 flex items-center gap-1"
                                    style={{ background: '#C97D60', border: 'none' }}
                                >
                                    <ShoppingCartOutlined className="text-xs" />
                                    Add
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

export default FoodCard;
